import type { ApiExportRequest, ApiListingRequest, ApiResponse } from '@bitstopco/bitstop-theme';

import api from '@/store/api';
import { athena, atlas } from '@/store/api/instances';
import type { QueueItem, QueueItemDetail } from '@/types';

interface ApproveQueueItemRequest {
  id: string;
}

interface RejectQueueItemRequest {
  id: string;
  reason: string;
}

interface BulkApproveQueueItemsRequest {
  ids: string[];
}

interface BulkRejectQueueItemsRequest {
  ids: string[];
  reason: string;
}

/**
 * RTK Query API for Marketing queue items.
 * Uses Atlas LocationQueue /v2/location-queue endpoints.
 */
export const queueApi = api.injectEndpoints({
  endpoints: (build) => ({
    getQueueItems: build.query<ApiResponse<QueueItem[]>, ApiListingRequest>({
      query: (data) =>
        athena({
          url: '/v2/marketing/queue',
          method: 'POST',
          data,
        }),
      providesTags: [{ type: 'MarketingQueue', id: 'LIST' }, 'MarketingQueue'],
    }),
    getQueueItem: build.query<ApiResponse<QueueItemDetail>, string>({
      query: (id) =>
        athena({
          url: `/v2/marketing/queue/${id}`,
          method: 'GET',
        }),
    }),
    approveQueueItem: build.mutation<ApiResponse<QueueItem>, ApproveQueueItemRequest>({
      query: ({ id }) =>
        atlas({
          url: `/v2/location-queue/${id}/approve`,
          method: 'POST',
        }),
      invalidatesTags: ['MarketingQueue', 'Locations'],
    }),
    rejectQueueItem: build.mutation<ApiResponse<QueueItem>, RejectQueueItemRequest>({
      query: ({ id, reason }) =>
        atlas({
          url: `/v2/location-queue/${id}/reject`,
          method: 'POST',
          data: { reason },
        }),
      invalidatesTags: ['MarketingQueue'],
    }),
    bulkApproveQueueItems: build.mutation<ApiResponse<void>, BulkApproveQueueItemsRequest>({
      query: (data) =>
        atlas({
          url: '/v2/location-queue/bulk-approve',
          method: 'POST',
          data,
        }),
      invalidatesTags: ['MarketingQueue'],
    }),
    bulkRejectQueueItems: build.mutation<ApiResponse<void>, BulkRejectQueueItemsRequest>({
      query: (data) =>
        atlas({
          url: '/v2/location-queue/bulk-reject',
          method: 'POST',
          data,
        }),
      invalidatesTags: ['MarketingQueue'],
    }),
    exportQueue: build.mutation<BlobPart, ApiExportRequest>({
      query: ({ type, data }) =>
        athena({
          url: `/v2/marketing/queue/${type}`,
          method: 'POST',
          data,
        }),
    }),
  }),
});

export const {
  useGetQueueItemsQuery,
  useGetQueueItemQuery,
  useApproveQueueItemMutation,
  useRejectQueueItemMutation,
  useBulkApproveQueueItemsMutation,
  useBulkRejectQueueItemsMutation,
  useExportQueueMutation,
} = queueApi;
